import { useSyncExternalStore } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Light / dark, for both surfaces.
//
// RESOLUTION, most specific first — deliberately the same shape as the language
// resolution next door in i18n.ts, because it answers the same question:
//
//   1. This screen's own choice, in localStorage. Two screens in one kitchen
//      can want different answers: the wall panel over the line lives in the
//      dark, the manager's laptop sits by a window. The choice must survive a
//      reboot, because nobody is going to re-pick it every morning.
//   2. The device's OS preference (`prefers-color-scheme`). Tablets set for
//      night shift already carry this, so honouring it means most screens are
//      right before anyone touches the button.
//   3. Dark. The historical default and the right one for a kitchen.
//
// The resolved theme is written to <html data-theme> rather than to a React
// context, so the CSS variables apply to the whole document — including the
// page background behind a short board, which a class on a component root
// would leave at the browser default.
//
// Applied at MODULE LOAD, before React renders, so there is no flash of the
// wrong theme on a slow tablet.
// ─────────────────────────────────────────────────────────────────────────────

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'matix_kds_theme';

type Listener = () => void;
const listeners = new Set<Listener>();

let override: Theme | null = readStored();

function readStored(): Theme | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'light' || raw === 'dark' ? raw : null;
  } catch {
    return null;
  }
}

function systemTheme(): Theme {
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

function snapshot(): Theme {
  return override ?? systemTheme();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Kept in step with the two palettes in styles/service.css. */
const PAGE_COLOR: Record<Theme, string> = { dark: '#0B1220', light: '#F1F4F9' };

function apply(): void {
  const theme = snapshot();
  try {
    document.documentElement.setAttribute('data-theme', theme);
    // The tablet's own chrome — status bar on an installed PWA, address bar on
    // Android — reads this. Leaving it navy behind a light board is the one
    // piece of the screen the CSS cannot reach.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', PAGE_COLOR[theme]);
  } catch {
    // No DOM (tests). The hooks below still report the right value.
  }
  for (const listener of listeners) listener();
}

/**
 * Set the per-screen theme. Passing null hands control back to the OS setting —
 * which is what makes a "follow the device" state reachable rather than a
 * one-way door into whichever mode was tapped first.
 */
export function setTheme(theme: Theme | null): void {
  override = theme;
  try {
    if (theme) localStorage.setItem(STORAGE_KEY, theme);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode / storage full. The choice still holds for this session.
  }
  apply();
}

/** True when this screen is following the OS rather than an explicit choice. */
export function isFollowingSystem(): boolean {
  return override === null;
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

// Follow the OS live, but only while no explicit choice is in force: a device
// that flips to dark at sunset should take the board with it.
try {
  const query = window.matchMedia('(prefers-color-scheme: light)');
  const onChange = (): void => {
    if (override === null) apply();
  };
  // Safari below 14 has no addEventListener on MediaQueryList.
  if (typeof query.addEventListener === 'function') query.addEventListener('change', onChange);
  else query.addListener(onChange);
} catch {
  // No matchMedia. The stored/default theme still applies.
}

apply();
